/**
 * Validar un número, como transacción propia.
 *
 * QUÉ CAMBIÓ. Antes esto no existía porque no hacía falta: pedir un código era
 * `supabase.auth.signInWithOtp`, o sea pedir una cuenta. Validar el número y
 * crear la sesión eran el mismo acto, y por eso no se podía hacer uno sin el
 * otro — ni verificar un teléfono sin registrarse, ni registrarse sin que el
 * proveedor de OTP fuera también el de autenticación.
 *
 * Ahora Verifik es dueño del ciclo del código y el servidor decide, por
 * separado, qué abre una validación: siempre un token de un solo uso que
 * autoriza el KYC, y —según con qué se llamó— atar el número a la sesión que ya
 * había, o emitir una nueva.
 *
 * NADA SALE SIN PASAR LA PUERTA. `canDeliverOtp` se comprueba acá otra vez, no
 * porque la pantalla no lo haga, sino porque este servicio es lo que cualquier
 * pantalla futura va a llamar, y la garantía tiene que vivir donde está la
 * petición, no donde está el botón.
 */
import type { Session } from '@supabase/supabase-js'
import { supabase } from '../supabase'
import { ServiceError } from '../errors/service-error'
import { canDeliverOtp, OTP_UNDELIVERABLE_CODE } from '../utils/otp-delivery'
import { canonicalPhone } from '../utils/phone-e164'
import { securityTelemetryService } from './security-telemetry.service'

/** Cuánto bloquear el reenvío cuando el servidor dice que ya mandó uno. */
export const OTP_RESEND_LOCKOUT_SECONDS = 120

export interface SendPhoneOtpResult {
  /** Segundos que vale el código. */
  expiresIn: number
  /** Segundos hasta poder pedir otro. */
  resendAfter: number
}

/** Qué hizo el servidor con la cuenta detrás del número. */
export type PhoneVerificationAccount =
  /** El número quedó atado a la sesión que ya existía. */
  | 'linked'
  /** Se emitió sesión: cuenta encontrada o recién creada. */
  | 'session'
  /** El número es de una cuenta con correo; hay que entrar por ahí. */
  | 'use_email_login'
  /** No hay cuenta con ese número y no se pidió crearla (inicio de sesión). */
  | 'not_found'
  /** El número se validó pero la cuenta no se pudo tocar. El token vale igual. */
  | 'unresolved'

export interface VerifyPhoneOtpResult {
  /** La prueba de propiedad del número. Es lo que el KYC exige. */
  verificationToken: string
  expiresIn: number
  phone: string
  account: PhoneVerificationAccount
  /**
   * La sesión YA INSTALADA, devuelta directamente.
   *
   * No es un detalle de comodidad. En nativo, la escritura a SecureStore puede
   * ir un instante detrás, así que un `getSession()` inmediatamente después de
   * instalar puede contestar null — y ese null, escrito en la caché de React
   * Query, borra una sesión viva y rebota a welcome a alguien que acaba de
   * crear su cuenta. Devolver el objeto quita la carrera en vez de correrla.
   */
  session: Session | null
}

/**
 * Cuánto esperar antes de reintentar, según lo que dijo el servidor.
 *
 * `Retry-After` es la única fuente honesta: el enfriamiento lo decide el
 * backend y el proveedor, no la pantalla. Cuando no viene, se usa el bloqueo de
 * 120 segundos, que es el que el servidor aplica por defecto — un contador que
 * adivina de menos es un botón que invita a gastar otro crédito.
 */
function retryAfterSeconds(response: Response | undefined): number {
  const header = response?.headers?.get?.('Retry-After')
  const parsed = header ? Number.parseInt(header, 10) : Number.NaN
  if (Number.isFinite(parsed) && parsed > 0) return Math.min(parsed, 3600)
  return OTP_RESEND_LOCKOUT_SECONDS
}

/** Lee `{ error, code }` del cuerpo que devuelve `secureErrorResponse`. */
async function readEdgeError(
  err: unknown,
): Promise<{ status: number; code: string | null; message: string | null; retryAfter: number }> {
  const context = (err as { context?: Response }).context
  const status = context?.status ?? 0
  const retryAfter = retryAfterSeconds(context)
  if (!context || typeof context.clone !== 'function') {
    return { status, code: null, message: null, retryAfter }
  }
  try {
    const body = (await context.clone().json()) as { error?: unknown; code?: unknown }
    return {
      status,
      code: typeof body?.code === 'string' && body.code ? body.code : null,
      message: typeof body?.error === 'string' && body.error ? body.error : null,
      retryAfter,
    }
  } catch {
    return { status, code: null, message: null, retryAfter }
  }
}

/**
 * Los códigos del servidor a claves de i18n.
 *
 * El código gana sobre la prosa siempre. La capa de verificación aprendió esto
 * al revés: elegía el mensaje buscando subcadenas en español dentro de la
 * respuesta, y reescribir una frase del servidor cambiaba lo que decía la app.
 */
const MESSAGE_KEYS: Record<string, string> = {
  PHONE_INVALID: 'auth.validation.phone_format',
  PHONE_UNDELIVERABLE: 'auth.errors.phone_undeliverable',
  OTP_RECENTLY_SENT: 'auth.errors.otp_recently_sent',
  OTP_RATE_LIMITED: 'auth.errors.otp_rate_limited',
  OTP_SERVICE_UNAVAILABLE: 'auth.errors.otp_service_unavailable',
  OTP_SEND_FAILED: 'auth.errors.otp_send_failed',
  OTP_INVALID: 'auth.errors.otp_invalid',
  OTP_EXPIRED: 'auth.errors.otp_expired',
  OTP_TOO_MANY_ATTEMPTS: 'auth.errors.otp_too_many_attempts',
  OTP_USE_EMAIL_LOGIN: 'auth.errors.phone_account_uses_email',
  PHONE_ACCOUNT_NOT_FOUND: 'auth.errors.phone_account_not_found',
  PHONE_IN_USE: 'auth.errors.phone_in_use',
  PHONE_CHANGE_NEEDS_CURRENT: 'auth.errors.phone_change_needs_current',
  PHONE_LINK_FAILED: 'auth.errors.phone_link_failed',
  SESSION_REQUIRED: 'auth.errors.session_missing',
  SESSION_STALE: 'auth.errors.session_missing',
}

class PhoneVerificationServiceImpl {
  private readonly pendingSends = new Map<string, Promise<SendPhoneOtpResult>>()
  // A successful OTP must survive a failed session refresh. A partially
  // committed final link can instead reconcile its existing Auth phone. Both
  // retries are scoped to the caller and expire with the ownership proof;
  // nothing is persisted, and reconciliation never serves as a current-phone
  // proof or returns a KYC verification token.
  private pendingLink: {
    userId: string
    phone: string
    intent: 'link' | 'change'
    proveCurrent: boolean
    expiresAt: number
    completed: boolean
  } | null = null

  async verifyPhoneLink(
    phone: string,
    code: string,
    {
      intent = 'link',
      proveCurrent = false,
    }: {
      intent?: 'link' | 'change'
      proveCurrent?: boolean
    } = {},
  ): Promise<{ account: 'linked'; phone: string; session: Session }> {
    const caller = await this.activeLinkSession()
    // La forma que usa el servidor, no "+" y los dígitos: un +1 escrito con su
    // troncal ("+1 1 201…") se vincula como "+1201…", y comparar contra lo
    // escrito convertía un vínculo hecho en un fallo (TAGO-MOBILE-53).
    const normalized = canonicalPhone(phone)
    if (!normalized || !canDeliverOtp(normalized)) {
      throw new ServiceError({
        message: 'Invalid phone link target',
        code: 'PHONE_INVALID',
        statusCode: 422,
        userMessageKey: 'auth.validation.phone_format',
      })
    }

    if (
      this.pendingLink?.userId !== caller.user.id ||
      this.pendingLink.phone !== normalized ||
      this.pendingLink.intent !== intent ||
      this.pendingLink.proveCurrent !== proveCurrent ||
      this.pendingLink.expiresAt <= Date.now()
    ) {
      this.pendingLink = null
    }

    const remember = (completed: boolean, expiresIn = 600) => {
      this.pendingLink = {
        userId: caller.user.id,
        phone: normalized,
        intent,
        proveCurrent,
        expiresAt: Date.now() + Math.min(expiresIn, 600) * 1000,
        completed,
      }
    }

    if (this.pendingLink && !this.pendingLink.completed && !proveCurrent) {
      const { data, error } = await supabase.functions.invoke('verify-phone-otp', {
        timeout: 45_000,
        body: { phone: normalized, intent, reconcileOnly: true },
        headers: { Authorization: `Bearer ${caller.access_token}` },
      })
      if (error) {
        const failure = await this.toServiceError(error, 'Could not finish the phone link')
        if (failure.code !== 'PHONE_LINK_NOT_APPLIED') throw failure
        this.pendingLink = null
      } else {
        if (data?.account !== 'linked' || canonicalPhone(data?.phone) !== normalized) {
          throw new ServiceError({
            message: 'Phone reconciliation did not confirm the requested link',
            code: 'PHONE_LINK_FAILED',
            userMessageKey: 'auth.errors.phone_link_failed',
          })
        }
        remember(true)
      }
    }

    if (!this.pendingLink?.completed) {
      try {
        const verified = await this.verifyOtp(normalized, code, {
          intent,
          linkSession: caller,
        })
        remember(true, verified.expiresIn)
      } catch (err) {
        // A timeout can hide a committed Auth write just as a projection
        // failure can. On retry, ask the server to reconcile first. This does
        // not authorize a new link, and the current-phone proof always needs
        // its real OTP to have completed successfully.
        if (!proveCurrent && err instanceof ServiceError && err.statusCode >= 500) {
          remember(false)
        }
        throw err
      }
    }

    const active = await this.activeLinkSession()
    if (active.user.id !== caller.user.id) {
      this.pendingLink = null
      throw ServiceError.unauthorized('Account changed while linking a phone')
    }
    const { data, error } = await supabase.auth.refreshSession()
    if (error || !data.session) {
      throw new ServiceError({
        message: 'Phone linked but session refresh failed',
        code: 'PHONE_LINK_REFRESH_FAILED',
        userMessageKey: 'auth.errors.phone_link_refresh_failed',
        cause: error,
      })
    }
    const session = data.session
    const latest = await this.activeLinkSession()
    if (session.user.id !== caller.user.id || latest.user.id !== caller.user.id) {
      this.pendingLink = null
      throw ServiceError.unauthorized('Account changed while refreshing the phone link')
    }
    if (!session.user.phone_confirmed_at || canonicalPhone(session.user.phone) !== normalized) {
      this.pendingLink = null
      throw new ServiceError({
        message: 'Refreshed Auth user does not own the requested phone',
        code: 'PHONE_LINK_FAILED',
        userMessageKey: 'auth.errors.phone_link_failed',
      })
    }
    this.pendingLink = null
    return { account: 'linked', phone: normalized, session }
  }

  private async activeLinkSession(): Promise<Session> {
    const { data, error } = await supabase.auth.getSession()
    if (error) throw ServiceError.fromSupabaseError(error)
    if (!data.session) throw ServiceError.unauthorized('Phone linking requires an active session')
    return data.session
  }

  /**
   * Pide el código por WhatsApp.
   *
   * @throws ServiceError con `details.retryAfterSeconds` cuando el servidor
   * impone una espera — el llamador la renderiza como bloqueo visual en vez de
   * dejar el botón vivo para que se toque otra vez.
   */
  async sendOtp(phone: string): Promise<SendPhoneOtpResult> {
    const pending = this.pendingSends.get(phone)
    if (pending) return pending
    const request = this.performSendOtp(phone)
    this.pendingSends.set(phone, request)
    try {
      return await request
    } finally {
      // A failed request must release the lock so a deliberate retry can work.
      if (this.pendingSends.get(phone) === request) this.pendingSends.delete(phone)
    }
  }

  private async performSendOtp(phone: string): Promise<SendPhoneOtpResult> {
    // La telemetría de contacto se emite acá y no en la pantalla, porque lo que
    // hay que poder ver desde afuera es el ENVÍO, no el toque. El día que el
    // proveedor anterior suspendió la cuenta nos enteramos por una persona, no
    // por un tablero: los rechazos del proveedor no dejaban rastro propio.
    securityTelemetryService.recordContactVerificationEvent({
      event: 'contact_verification_send_requested',
      contactType: 'phone',
      flow: 'phone_otp',
      channel: 'whatsapp',
      target: phone,
    })

    // La puerta, en el servicio. Cero red para un número que no puede recibir.
    if (!canDeliverOtp(phone)) {
      const blocked = new ServiceError({
        message: `Undeliverable number ${phone.slice(0, 6)}… — provider not called`,
        code: OTP_UNDELIVERABLE_CODE,
        statusCode: 422,
        userMessageKey: 'auth.errors.phone_undeliverable',
      })
      securityTelemetryService.recordContactVerificationEvent({
        event: 'contact_verification_send_failed',
        contactType: 'phone',
        flow: 'phone_otp',
        channel: 'whatsapp',
        target: phone,
        error: blocked,
      })
      throw blocked
    }

    const { data, error } = await supabase.functions.invoke('send-phone-otp', {
      body: { phone },
      // Includes the provider's bounded retry plus the database checks.
      timeout: 60_000,
    })

    const validTiming = (value: unknown, minimum: number) =>
      value === undefined ||
      (typeof value === 'number' && Number.isFinite(value) && value >= minimum && value <= 3600)
    if (
      error ||
      data?.ok !== true ||
      !validTiming(data?.expiresIn, 1) ||
      !validTiming(data?.resendAfter, 0)
    ) {
      const failure = error
        ? await this.toServiceError(error, 'No pudimos enviar el código')
        : new ServiceError({
            message: 'OTP send response did not confirm delivery',
            code: 'OTP_SEND_FAILED',
            statusCode: 502,
            userMessageKey: 'auth.errors.otp_send_failed',
          })
      securityTelemetryService.recordContactVerificationEvent({
        event: 'contact_verification_send_failed',
        contactType: 'phone',
        flow: 'phone_otp',
        channel: 'whatsapp',
        target: phone,
        error: failure,
      })
      throw failure
    }

    securityTelemetryService.recordContactVerificationEvent({
      event: 'contact_verification_sent',
      contactType: 'phone',
      flow: 'phone_otp',
      channel: 'whatsapp',
      target: phone,
    })

    const payload = (data ?? {}) as { expiresIn?: number; resendAfter?: number }
    return {
      expiresIn: payload.expiresIn ?? 300,
      resendAfter: payload.resendAfter ?? OTP_RESEND_LOCKOUT_SECONDS,
    }
  }

  /**
   * Resuelve el código.
   *
   * Cuando el servidor emite sesión, se instala acá: el resto de la app lee la
   * sesión de Supabase y no debería enterarse de que vino por otro camino.
   */
  async verifyOtp(
    phone: string,
    code: string,
    options: {
      /**
       * Crear la cuenta si el número es desconocido. Solo en el registro: en el
       * inicio de sesión, un número que no existe es una respuesta, no una
       * invitación a fabricar una cuenta que nadie pidió.
       */
      allowCreate?: boolean
      /**
       * Que vino a hacer quien llama. El servidor exige que coincida con lo que
       * ve: un cliente puede mentir sobre lo que quiere, no sobre si trae
       * sesion. Sin esto, el servidor tenia que adivinar mirando un header, y
       * de ahi salia que una sesion valida mas el OTP de cualquier numero
       * bastaba para quedarse con ese numero.
       */
      intent?: 'signup' | 'login' | 'link' | 'change'
      metadata?: { preferredLocale?: 'es' | 'en'; acceptedTerms?: boolean }
      /** Bind an in-flight contact mutation to the session that started it. */
      linkSession?: Session
    } = {},
  ): Promise<VerifyPhoneOtpResult> {
    securityTelemetryService.recordContactVerificationEvent({
      event: 'contact_verification_verify_requested',
      contactType: 'phone',
      flow: 'phone_otp',
      channel: 'whatsapp',
      target: phone,
    })

    const { data, error } = await supabase.functions.invoke('verify-phone-otp', {
      timeout: 45_000,
      body: {
        phone,
        code,
        allowCreate: options.allowCreate === true,
        intent: options.intent,
        metadata: options.metadata,
      },
      ...(options.linkSession
        ? { headers: { Authorization: `Bearer ${options.linkSession.access_token}` } }
        : {}),
    })

    if (error) {
      const failure = await this.toServiceError(error, 'No pudimos validar el código')
      securityTelemetryService.recordContactVerificationEvent({
        event: 'contact_verification_verify_failed',
        contactType: 'phone',
        flow: 'phone_otp',
        channel: 'whatsapp',
        target: phone,
        error: failure,
      })
      throw failure
    }

    const payload = (data ?? {}) as Partial<VerifyPhoneOtpResult>
    // El número confirmado se compara en su forma canónica, la misma que el
    // servidor devuelve. Canónica no es más laxa: otro número sigue fallando, y
    // un valor que no es un número no confirma nada aunque el otro tampoco lo sea.
    const requestedPhone = canonicalPhone(phone)
    if (
      (options.intent === 'link' || options.intent === 'change') &&
      (payload.account !== 'linked' ||
        !requestedPhone ||
        canonicalPhone(payload.phone) !== requestedPhone ||
        payload.session != null)
    ) {
      throw new ServiceError({
        message: 'OTP verified without confirming the account phone link',
        code: 'PHONE_LINK_FAILED',
        userMessageKey: 'auth.errors.phone_link_failed',
      })
    }
    if (!payload.verificationToken) {
      throw new ServiceError({
        message: 'verify-phone-otp respondió sin token de verificación',
        code: 'OTP_FAILED',
        statusCode: 502,
        userMessageKey: 'auth.errors.otp_send_failed',
      })
    }

    let session: Session | null = null
    const issued = payload.session as { access_token?: string; refresh_token?: string } | null
    if (issued?.access_token && issued?.refresh_token) {
      const { data: installed, error: sessionError } = await supabase.auth.setSession({
        access_token: issued.access_token,
        refresh_token: issued.refresh_token,
      })
      // La validación del número SÍ ocurrió. Tirar acá se la cobraría otra vez
      // a la persona por un fallo de instalación de sesión, así que se reporta
      // como `unresolved` y el token —que es lo que abre el KYC— sobrevive.
      if (sessionError) {
        return {
          verificationToken: payload.verificationToken,
          expiresIn: payload.expiresIn ?? 600,
          phone: payload.phone ?? phone,
          account: 'unresolved',
          session: null,
        }
      }
      session = installed.session ?? null
    }

    securityTelemetryService.recordContactVerificationEvent({
      event: 'contact_verification_verified',
      contactType: 'phone',
      flow: 'phone_otp',
      channel: 'whatsapp',
      target: phone,
    })

    return {
      verificationToken: payload.verificationToken,
      expiresIn: payload.expiresIn ?? 600,
      phone: payload.phone ?? phone,
      account: (payload.account as PhoneVerificationAccount) ?? 'unresolved',
      session,
    }
  }

  private async toServiceError(err: unknown, fallback: string): Promise<ServiceError> {
    const { status, code, message, retryAfter } = await readEdgeError(err)
    return new ServiceError({
      message: message ?? fallback,
      code: code ?? 'OTP_FAILED',
      statusCode: status || 500,
      userMessageKey: code ? (MESSAGE_KEYS[code] ?? null) : null,
      // El llamador lo convierte en el contador. Sin esto, una espera del
      // servidor llega como un fallo genérico y el botón queda vivo.
      details: { retryAfterSeconds: retryAfter },
      cause: err,
    })
  }
}

export const phoneVerificationService = new PhoneVerificationServiceImpl()
